import html_default from "./template.default.html?raw";
const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("default" == app_theme) {
  html = html_default;
}
import { inject, ref, onMounted, computed, type SetupContext } from "vue";
import CheckoutStepper, { type StepperItem } from "../CheckoutStepper/index.ts";
import CheckoutStart from "../CheckoutStart/index.ts";
import CheckoutWho from "../CheckoutWho/index.ts";
import CheckoutCustomerAddress from "../CheckoutCustomerAddress/index.ts";
import CheckoutPartnerAddress from "../CheckoutPartnerAddress/index.ts";
import CheckoutConfirm from "../CheckoutConfirm/index.ts";
import CheckoutComplete from "../CheckoutComplete/index.ts";
import TsmSpinner from "../TsmSpinner/index.ts";
import type { CourseService } from "../../application/services/CourseService.ts";
import type { CartService } from "../../application/services/CartService.ts";
import type { UserService } from "../../application/services/UserService.ts";
import type { CustomerService } from "../../application/services/CustomerService.ts";
import type { CourseDto } from "../../shared/CourseDto.ts";
import type { CourseLessonDto } from "../../shared/CourseLessonDto.ts";
import type { CartDto } from "../../shared/CartDto.ts";
import type { CartPatchDto } from "../../shared/CartPatchDto.ts";
import type { PartyAddressDto } from "../../shared/PartyAddressDto.ts";
import type { CartSubmitDto } from "../../shared/CartSubmitDto.ts";
import type { CustomerMeDto } from "../../shared/CustomerMeDto.ts";

interface Props {
  courseId: number;
}

type Step = "CheckoutStart" | "CheckoutWho" | "CheckoutCustomerAddress" | "CheckoutPartnerAddress" | "CheckoutConfirm" | "CheckoutComplete";

// Ablauf: 1 Kurs (Einzel/Paar) -> 2 Wer bucht (Code-Login oder neu, entfaellt bei Login) -> 3 Angaben (Besteller, Partner)
// -> 4 Pruefen -> 5 Fertig. Der Warenkorb liegt in der API, die Referenz im localStorage.
export default {
  template: html,
  components: {
    CheckoutStepper,
    CheckoutStart,
    CheckoutWho,
    CheckoutCustomerAddress,
    CheckoutPartnerAddress,
    CheckoutConfirm,
    CheckoutComplete,
    TsmSpinner,
  },
  props: {
    courseId: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  emits: {
    onCourse: (name: string) => typeof name === "string",
    onClose: () => true,
  },
  setup(props: Props, { emit }: SetupContext) {
    const error = ref("");
    const initialized = ref(false);
    const busy = ref(false);
    const course = ref<CourseDto | null>(null);
    const courseLesson = ref<CourseLessonDto[]>([]);
    const cart = ref<CartDto | null>(null);
    const currentStep = ref<Step>("CheckoutStart");
    const validationErrors = ref({});
    const isLoggedIn = ref(false);
    const me = ref<CustomerMeDto | null>(null);
    const guestEmail = ref("");
    const loginEmail = ref("");
    const courseService: CourseService | undefined = inject("courseService");
    const cartService: CartService | undefined = inject("cartService");
    const userService: UserService | undefined = inject("userService");
    const customerService: CustomerService | undefined = inject("customerService");

    const currentCourseId = computed(function () {
      return typeof props.courseId === "number" && props.courseId > 0 ? props.courseId : 0;
    });

    const stepperItems = computed<StepperItem[]>(function () {
      const items: StepperItem[] = [{ key: "course", label: "Kurs" }];
      if (!isLoggedIn.value) {
        items.push({ key: "who", label: "Wer bucht" });
      }
      items.push({ key: "details", label: "Angaben" }, { key: "review", label: "Prüfen" }, { key: "done", label: "Fertig" });
      return items;
    });

    const stepperCurrent = computed(function () {
      switch (currentStep.value) {
        case "CheckoutStart":
          return "course";
        case "CheckoutWho":
          return "who";
        case "CheckoutCustomerAddress":
        case "CheckoutPartnerAddress":
          return "details";
        case "CheckoutConfirm":
          return "review";
        default:
          return "done";
      }
    });

    const needsPartnerStep = computed(function () {
      return course.value?.courseType == 2 && (cart.value?.isPairBooking ?? false);
    });

    const refreshLogin = async function () {
      isLoggedIn.value = false;
      me.value = null;
      if (!userService) {
        return;
      }
      try {
        const info = await userService.getUserInfo();
        isLoggedIn.value = info?.isLoggedIn ?? false;
        if (isLoggedIn.value && customerService) {
          me.value = await customerService.me();
        }
      } catch {
        isLoggedIn.value = false;
      }
    };

    const reloadCart = async function () {
      if (cartService) {
        try {
          cart.value = await cartService.getCart();
        } catch {
          cart.value = null;
        }
      }
    };

    const fail = function (err: unknown) {
      error.value = (err as Error).message;
    };

    const run = async function (action: () => Promise<void>) {
      error.value = "";
      validationErrors.value = {};
      busy.value = true;
      try {
        await action();
      } catch (err) {
        fail(err);
      } finally {
        busy.value = false;
      }
    };

    // Schritt 1: Einzel oder Paar
    const onStartResponse = async function (isPair: boolean) {
      await run(async function () {
        if (!cartService) {
          throw new Error("Systemfehler: CartService undefined");
        }
        if (cart.value == null) {
          cart.value = await cartService.createCart(currentCourseId.value, isPair);
          if (cart.value == null) {
            throw new Error("Warenkorb konnte nicht erstellt werden");
          }
        } else {
          cart.value = await cartService.updateCart({ courseId: currentCourseId.value, isPairBooking: isPair } as CartPatchDto);
        }
        currentStep.value = isLoggedIn.value ? "CheckoutCustomerAddress" : "CheckoutWho";
      });
    };

    // Schritt 2: Login mit Code erfolgreich
    const onLoggedIn = async function () {
      await run(async function () {
        await refreshLogin();
        await reloadCart();
        currentStep.value = "CheckoutCustomerAddress";
      });
    };

    // Schritt 2: als neue Person weiter (E-Mail evtl. schon eingegeben)
    const onNew = function (email: string) {
      guestEmail.value = email;
      loginEmail.value = "";
      currentStep.value = "CheckoutCustomerAddress";
    };

    // Gast-Formular: E-Mail ist bekannt, Kunde will sich doch anmelden
    const onWantLogin = function (email: string) {
      loginEmail.value = email;
      currentStep.value = "CheckoutWho";
    };

    const afterCustomerStep = function () {
      currentStep.value = needsPartnerStep.value ? "CheckoutPartnerAddress" : "CheckoutConfirm";
    };

    // Schritt 3: Angaben des Bestellers gespeichert
    const onCustomerAddressChange = async function (val: PartyAddressDto) {
      await run(async function () {
        if (!cartService) {
          throw new Error("Systemfehler: CartService undefined");
        }
        cart.value = await cartService.setCustomerAddress(val);
        afterCustomerStep();
      });
    };

    // Schritt 3: eingeloggter Kunde bestaetigt seine Stammdaten ohne Aenderung
    const onCustomerConfirmed = async function () {
      await run(async function () {
        await reloadCart();
        afterCustomerStep();
      });
    };

    const onPartnerAddressChange = async function (val: PartyAddressDto) {
      await run(async function () {
        if (!cartService) {
          throw new Error("Systemfehler: CartService undefined");
        }
        cart.value = await cartService.setPartnerAddress(val);
        currentStep.value = "CheckoutConfirm";
      });
    };

    const onCartChange = async function (patch: CartPatchDto) {
      await run(async function () {
        if (cartService) {
          cart.value = await cartService.updateCart(patch);
          if (currentStep.value == "CheckoutPartnerAddress") {
            currentStep.value = "CheckoutConfirm";
          }
        }
      });
    };

    const onCartSubmit = async function (subm: CartSubmitDto) {
      await run(async function () {
        if (cartService) {
          cart.value = await cartService.submitCart(subm);
          currentStep.value = "CheckoutComplete";
        }
      });
    };

    const goBack = function () {
      error.value = "";
      switch (currentStep.value) {
        case "CheckoutWho":
          currentStep.value = "CheckoutStart";
          break;
        case "CheckoutCustomerAddress":
          currentStep.value = isLoggedIn.value ? "CheckoutStart" : "CheckoutWho";
          break;
        case "CheckoutPartnerAddress":
          currentStep.value = "CheckoutCustomerAddress";
          break;
        case "CheckoutConfirm":
          currentStep.value = needsPartnerStep.value ? "CheckoutPartnerAddress" : "CheckoutCustomerAddress";
          break;
        default:
          break;
      }
    };

    const canGoBack = computed(() => currentStep.value != "CheckoutStart" && currentStep.value != "CheckoutComplete");

    onMounted(async () => {
      try {
        if (courseService === undefined) {
          throw new Error("Systemfehler: CourseService is null");
        }
        course.value = await courseService.getById(currentCourseId.value);
        if (course.value == null) {
          throw new Error("Kurs nicht gefunden");
        }
        emit("onCourse", course.value.name);
        courseLesson.value = await courseService.getLessons(currentCourseId.value);
      } catch (err) {
        fail(err);
      }
      await refreshLogin();
      if (cartService) {
        try {
          const oldCart = await cartService.getCart();
          if (oldCart != null && oldCart.ordered == null && oldCart.processed == null && oldCart.courseId == currentCourseId.value) {
            cart.value = oldCart;
          } else {
            cart.value = null;
            await cartService.clearCart();
          }
        } catch {
          cart.value = null;
          await cartService.clearCart();
        }
      }
      initialized.value = true;
    });

    return {
      error,
      busy,
      course,
      courseLesson,
      cart,
      currentStep,
      initialized,
      isLoggedIn,
      me,
      guestEmail,
      loginEmail,
      stepperItems,
      stepperCurrent,
      canGoBack,
      goBack,
      onStartResponse,
      onLoggedIn,
      onNew,
      onWantLogin,
      onCustomerAddressChange,
      onCustomerConfirmed,
      onPartnerAddressChange,
      onCartChange,
      onCartSubmit,
      validationErrors,
      closeDialog: () => emit("onClose"),
    };
  },
};
